<script setup>
import FoodFlowModal from '@/components/FoodFlow/FoodFlowModal.vue';
import { ref, computed } from 'vue';
import { useContactStore } from '@/stores/contact';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    contact: {
        type: Object,
        default: () => ({
            name: '',
            role: 'supplier',
            type_id: null,
            is_active: true,
            is_preferred: false,
            color: null,
            address: '',
            city: '',
            postal_code: '',
            phone: '',
            notes: '',
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const form = ref({ ...props.contact });

const { contactTypeOptions } = storeToRefs(useOptionsStore());

const contactStore = useContactStore();
const { storeContact, updateContact } = contactStore;

const isEditing = computed(() => props.mode === 'Edit' && !!props.contact.hash);
const modalTitle = computed(() => isEditing.value ? trans('label.edit') : trans('label.add'));

const colorPickerValue = computed({
    get: () => form.value.color ?? '#ffffff',
    set: (value) => {
        form.value.color = value;
    },
});

const clearColor = () => {
    form.value.color = null;
};

const save = async () => {
    try {
        const action = props.mode === 'Create' ? storeContact : updateContact;

        await action({
            ...form.value,
            hash: props.contact.hash,
        });

        emit('close-modal');
    } catch (error) {
        // Validation and server errors are surfaced centrally via the error store and toast interceptors.
    }
};

const toggleStatus = async () => {
    try {
        await updateContact({
            ...form.value,
            hash: props.contact.hash,
            is_active: !form.value.is_active,
        });

        emit('close-modal');
    } catch (error) {
        // Validation and server errors are surfaced centrally via the error store and toast interceptors.
    }
};
</script>

<template>
<FoodFlowModal>
    <template #header>{{ modalTitle }} {{ $t('label.contact') }}</template>

    <form class="flex flex-col gap-4" @submit.prevent="save">
        <div>
            <label class="foodflow-field-label">{{ $t('label.name') }}</label>
            <input v-model="form.name" type="text" class="foodflow-field-input">
        </div>
        <label class="contact-preferred-toggle">
            <input v-model="form.is_preferred" type="checkbox" class="contact-preferred-checkbox">
            <span>
                <span class="contact-preferred-label">{{ $t('label.preferred') }}</span>
                <span class="contact-preferred-hint">{{ $t('label.preferred_contact_hint') }}</span>
            </span>
        </label>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
                <label class="foodflow-field-label">{{ $t('label.role') }}</label>
                <select v-model="form.role" class="foodflow-field-input">
                    <option value="supplier">{{ $t('label.supplier') }}</option>
                    <option value="recipient">{{ $t('label.recipient') }}</option>
                    <option value="both">{{ $t('label.both') }}</option>
                </select>
            </div>
            <div>
                <label class="foodflow-field-label">{{ $t('label.status') }}</label>
                <select v-model="form.is_active" class="foodflow-field-input">
                    <option :value="true">{{ $t('label.active') }}</option>
                    <option :value="false">{{ $t('label.inactive') }}</option>
                </select>
            </div>
        </div>
        <div>
            <label class="foodflow-field-label">{{ $t('label.category') }}</label>
            <select v-model="form.type_id" class="foodflow-field-input">
                <option v-for="type in contactTypeOptions" :key="type.id" :value="type.id">{{ type.name }}</option>
            </select>
        </div>
        <div>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <label class="foodflow-field-label">{{ $t('label.address') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
            <input v-model="form.address" type="text" class="foodflow-field-input">
        </div>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <label class="foodflow-field-label">{{ $t('label.city') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
                <input v-model="form.city" type="text" class="foodflow-field-input">
            </div>
            <div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <label class="foodflow-field-label">{{ $t('label.postal_code') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
                <input
                    v-model="form.postal_code"
                    type="text"
                    maxlength="10"
                    class="foodflow-field-input">
            </div>
        </div>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <label class="foodflow-field-label">{{ $t('label.phone') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
                <input
                    v-model="form.phone"
                    type="tel"
                    maxlength="20"
                    class="foodflow-field-input">
            </div>
            <div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <label class="foodflow-field-label">{{ $t('label.tile_color') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
                <div class="flex items-center gap-2.5">
                    <input v-model="colorPickerValue" type="color" class="contact-color-swatch">
                    <template v-if="form.color">
                        <span class="contact-color-value">{{ form.color }}</span>
                        <button type="button" class="foodflow-btn-muted" @click="clearColor">{{ $t('label.clear') }}</button>
                    </template>
                </div>
            </div>
        </div>
        <div>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <label class="foodflow-field-label">{{ $t('label.notes') }} <span class="foodflow-field-optional">({{ $t('label.optional') }})</span></label>
            <input v-model="form.notes" type="text" class="foodflow-field-input">
        </div>

        <div v-if="isEditing" class="flex items-center gap-3.5 rounded-[10px] bg-[#F7F4EF] p-3.5">
            <div class="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border border-[#EDE8DF] bg-white">
                <svg
                    width="36"
                    height="36"
                    viewBox="0 0 36 36"
                    fill="none"><rect
                        x="2"
                        y="2"
                        width="13"
                        height="13"
                        rx="1"
                        stroke="#375475"
                        stroke-width="2"
                        fill="none" /><rect
                            x="5.5"
                            y="5.5"
                            width="6"
                            height="6"
                            fill="#375475" /><rect
                                x="21"
                                y="2"
                                width="13"
                                height="13"
                                rx="1"
                                stroke="#375475"
                                stroke-width="2"
                                fill="none" /><rect
                                    x="24.5"
                                    y="5.5"
                                    width="6"
                                    height="6"
                                    fill="#375475" /><rect
                                        x="2"
                                        y="21"
                                        width="13"
                                        height="13"
                                        rx="1"
                                        stroke="#375475"
                                        stroke-width="2"
                                        fill="none" /><rect
                                            x="5.5"
                                            y="24.5"
                                            width="6"
                                            height="6"
                                            fill="#375475" /><rect
                                                x="21"
                                                y="21"
                                                width="6"
                                                height="6"
                                                fill="#375475" /><rect
                                                    x="29"
                                                    y="21"
                                                    width="6"
                                                    height="6"
                                                    fill="#375475" /><rect
                                                        x="21"
                                                        y="29"
                                                        width="6"
                                                        height="6"
                                                        fill="#375475" /><rect
                                                            x="29"
                                                            y="29"
                                                            width="6"
                                                            height="6"
                                                            fill="#375475" /></svg>
            </div>
            <div class="text-[12px] leading-relaxed text-[#64748b]">
                {{ $t('label.qr_auto_generated') }}<br>
                <span class="font-mono text-[11px] font-bold text-[#375475]">{{ contact.hash }}</span>
            </div>
        </div>
    </form>

    <template #footer>
        <div>
            <button
                v-if="isEditing"
                type="button"
                class="rounded-lg bg-[#fde8e7] px-3.5 py-1.5 text-[12px] font-semibold text-[#a83430] hover:bg-[#fcd5d4]"
                @click="toggleStatus">
                {{ form.is_active ? $t('label.deactivate') : $t('label.reactivate') }}
            </button>
        </div>
        <div class="flex gap-2.5">
            <button type="button" class="rounded-[10px] bg-[#EDE8DF] px-5 py-2.5 text-[14px] font-semibold text-[#64748b] hover:bg-[#d5cfc4]" @click="$emit('cancel-modal')">{{ $t('label.cancel') }}</button>
            <button type="button" class="rounded-[10px] bg-[#375475] px-6 py-2.5 text-[14px] font-bold text-white transition-colors hover:bg-[#4a6d93]" @click="save">{{ $t('label.save_contact') }}</button>
        </div>
    </template>
</FoodFlowModal>
</template>
